import { Shadow } from '@genial/components';
import { BoxRadius, Color, Spacing } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { Box, FlatList } from 'native-base';
import React from 'react';
import { StyleSheet } from 'react-native';

import { SelectedProduct } from '../../../../store/product/types';
import { OperationType } from '../../../BatchTicket/components/DynamicAsset/contexts/constants';
import { LateralBar } from '../../../NewApplication/Product/components/LateralBar';
import { OrderOfferResponseProps, OrderResponseProps, ProductType } from '../../utils';
import { SectionContainer } from '../SectionContainer';
import { SectionItem } from '../SectionItem';

import { createSectionProductItems, getBackgroundColor } from './constants';

type OrderOffer = OrderOfferResponseProps & OrderResponseProps;

type ProductItem = {
  label?: string;
  value: string | React.JSX.Element;
  preventLastHandler?: boolean;
  mobileValue?: string;
};

export interface ProductSectionProps extends Partial<OrderOffer> {
  productType: ProductType;
  operation?: string;
  operationType?: OperationType;
  fullProductItem?: SelectedProduct;
}

const numColumnsSizes = {
  FixedIncome: { base: 1, sm: 5, lg: 5 },
  FixedIncomeRedemption: { base: 1, sm: 4, lg: 7 },
  Fund: { base: 1, sm: 4, lg: 7 },
  FundRedemption: { base: 1, sm: 3, lg: 7 },
  Equity: { base: 1, sm: 4, lg: 8 },
  DirectTreasury: { base: 1, sm: 3, lg: 6 },
  StructuredOperations: { base: 1, sm: 4, lg: 8 },
};

const style = StyleSheet.create({
  columnWrapper: { justifyContent: 'space-between' },
});

const getProductType = (productType: ProductType, operation: OperationType) => {
  if (productType === 'Fund' && operation === 'sell') {
    return 'FundRedemption';
  }
  if (productType === 'FixedIncome' && operation === 'sell') {
    return 'FixedIncomeRedemption';
  }
  return productType || 'FixedIncome';
};

export const ProductSection: React.FC<ProductSectionProps> = ({
  productType: productSectionType,
  operationType,
  ...props
}) => {
  const device = useBreakpointValue({ base: 'mobile', sm: 'tablet', lg: 'desktop' });

  const sectionItems = createSectionProductItems({
    productType: productSectionType,
    ...props,
    device,
    operation: operationType,
    operationType: operationType as OperationType,
  }) as ProductItem[];

  const productType = getProductType(productSectionType, operationType as OperationType);

  const numColumns = useBreakpointValue(numColumnsSizes[productType || 'FixedIncome']);

  const hasColumnWrapperStyle = device === 'desktop' && numColumns !== 1;
  const columnWrapperStyle = style.columnWrapper;

  return (
    <SectionContainer title="Detalhes do produto">
      <Shadow level={5} borderRadius={BoxRadius.sm}>
        <LateralBar
          backgroundColor={
            Color[getBackgroundColor(productSectionType, operationType as 'buy' | 'sell')]
          }
          borderBottomLeftRadius={BoxRadius.sm}
          borderTopLeftRadius={BoxRadius.sm}
        />

        <Box px={Spacing['inline.xxxs']} borderRadius={BoxRadius.sm} py={Spacing['inline.xxs']}>
          <FlatList
            key={numColumns}
            numColumns={numColumns}
            keyExtractor={(_, index) => index.toString()}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ rowGap: 6 * numColumns }}
            data={sectionItems}
            {...(hasColumnWrapperStyle && { columnWrapperStyle })}
            renderItem={({ item, index }) => {
              const isLast =
                !item.preventLastHandler &&
                numColumns === numColumnsSizes[productType].lg &&
                index === sectionItems.length - 1;

              return (
                <Box {...(!isLast && { flex: 1 / numColumns })}>
                  <SectionItem {...item} testID={item.label || index.toString()} isLast={isLast} />
                </Box>
              );
            }}
          />
        </Box>
      </Shadow>
    </SectionContainer>
  );
};
